import { useEffect, useRef } from "react";
import { TopBar, DestinationRow, CoachRibbon } from "./kit";
import { useChromelessOverride } from "../context/ChromelessOverrideContext";
import { useAuth } from "../context/AuthContext";
import { newUi } from "../lib/featureFlags";
import { homePathForRole, homeLabelForRole } from "../lib/homePath";
import { AccessibilityMenu } from "./AccessibilityMenu";
import { AccountMenu } from "./AccountMenu";
import { NotificationsBell } from "./NotificationsBell";

/**
 * Issue #1120 (D221 O1, epic #1045) — the new-look chrome for `/learn` and
 * the guide pages, for a PLAYER. Until now these were the only player routes
 * still inside the old `Layout` shell: a white header with the old avatar, a
 * sidebar at `md:` and a six-item navy strip on a phone, which together with
 * the ribbon made two menu systems on one screen (G2) with 29px items (G4).
 *
 * There is no `learn` flag id and D202 makes one pointless, so the gate keys
 * off the record screen, the same way `Layout.jsx` keys the nav off
 * `season-record`: `newUi("record") && role === "player"`. A coach gets the
 * same chrome with the coach ribbon (#1121, `useLearnCoachNewLook` below); an
 * admin keeps Layout and gets `children` and nothing else, so their render is
 * unchanged. The page BODY is the player's concern only (`useLearnNewLook`
 * stays player-only): a coach's nine tabs are unchanged.
 *
 * The text-size control stays for the player (D249): the body still sizes in
 * rem, so the reason it was retired on rebuilt screens (#797) does not apply.
 * `compact` gives the bar's visuals and keeps text size, as in `MatchesScreen`.
 *
 * The page body is not redrawn here (that is #1128, waiting for Martin): the
 * children sit under the ribbon in a 20px frame, as `MatchesScreen` frames
 * its own.
 *
 * FOCUS (#1050 F4). Neither `MatchesScreen` nor `SettingsScreen` manages
 * focus on arrival and `ScrollToTop` only scrolls, so this component does
 * it: when the page passes a `title`, a visually hidden `h1` is rendered and
 * focused once on mount (`tabIndex={-1}`, so it is a target and not a tab
 * stop), instead of focus falling back to BODY after a keyboard choice on
 * the ribbon. The guide page has its own `h1` and passes no `title`.
 */
export function useLearnNewLook() {
  const { user } = useAuth();
  return newUi("record") && user?.role === "player";
}

// Issue #1121 — a coach gets the same chrome with the coach ribbon, on the
// same rollout configuration that draws the coach portal's own chrome (both
// `coach-portal-empty` and `squad` on, `lib/coachChromeGate.js`): the coach
// has no `record` screen, and a ribbon on /learn that the portal does not
// carry would be a menu appearing and vanishing between two coach screens.
export function useLearnCoachNewLook() {
  const { user } = useAuth();
  return newUi("coach-portal-empty") && newUi("squad") && user?.role === "coach";
}

/**
 * Issue #1128 (D217): the two-section `/learn` is the new look for a player AND
 * a coach; with neither look on (the flag off, an admin) `/learn` is the
 * shipped ten-tab page. The guide page's Back reads the same answer.
 */
export function useLearnSectionsLook() {
  const player = useLearnNewLook();
  const coach = useLearnCoachNewLook();
  return player || coach;
}

export default function LearnChrome({ title, children }) {
  const { user } = useAuth();
  const playerLook = useLearnNewLook();
  const coachLook = useLearnCoachNewLook();
  const active = playerLook || coachLook;
  const { setActive: setChromeless } = useChromelessOverride();
  const headingRef = useRef(null);

  useEffect(() => {
    if (!active) return undefined;
    setChromeless(true);
    return () => setChromeless(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  useEffect(() => {
    if (active && title && headingRef.current) headingRef.current.focus({ preventScroll: true });
  }, [active, title]);

  if (!active) return <>{children}</>;

  return (
    <div data-testid="learn-page" style={{ color: "var(--nw-ink)" }}>
      <span data-testid="learn-new-screen" className="sr-only" aria-hidden="true" />
      <TopBar
        variant="white"
        homeHref={homePathForRole(user?.role)}
        homeLabel={`CricketCompass — go to ${homeLabelForRole(user?.role)}`}
        endControls={
          <>
            <NotificationsBell compact surface="white" />
            <AccessibilityMenu compact surface="white" />
            <AccountMenu empty surface="white" hasRibbon />
          </>
        }
        data-testid="learn-topbar"
      />
      {coachLook ? <CoachRibbon current="learn" /> : <DestinationRow current="learn" />}
      {title && (
        <h1 ref={headingRef} tabIndex={-1} className="sr-only" data-testid="learn-heading">
          {title}
        </h1>
      )}
      <div style={{ padding: 20 }}>{children}</div>
    </div>
  );
}
